import React, { forwardRef, useId } from 'react';
import { LockIcon } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/Components/ui/tooltip';
import useGrants, { gateReason } from '@/Hooks/useGrants';

/**
 * A Button that knows why it is disabled.
 *
 * The server refuses regardless; this stops the user finding out from a
 * silent 403 — a refused XHR never becomes a flash (the layout's FlashToaster
 * only shows what a redirect carried), so a dead button with a reason is
 * the honest UX. The reason is stated, not hinted: by default it
 * sits beside the button in plain text (`reasonPlacement="inline"`), and is
 * also the tooltip. Where a row of small controls has no room for a sentence
 * per button — an API-key row, say — `reasonPlacement="tooltip"` keeps the
 * text for assistive technology and the hover only; the same page should then
 * state the reason once somewhere it can be read.
 *
 * The wording is useGrants().reasonFor's, so a menu item and a button say
 * the same thing about the same gap — and a suspended team hears about the
 * suspension, not about a permission its role never lost. `reason` is the
 * caller's wording for the permission sentence alone.
 *
 * `blocked` / `blockedReason` cover a refusal that is not about grants or
 * suspension (an unpaid plan, a rule of the page); it wins because it is the
 * broader fact.
 *
 * `creates` marks an entry point that makes something new — a passport, an
 * import, a key, an invitation. A create-suspended team keeps its grants, so
 * the ability check alone would light up a button the server is about to
 * refuse. `issues` marks the ones that issue passports — a root, a child, an
 * import — which a supplier team (one without a company) never may.
 *
 * Enabled, it renders the plain Button and nothing else, so the visual is
 * identical to every ungated button around it.
 */
const GatedButton = forwardRef(function GatedButton(
    {
        ability,
        ontologyId = null,
        reason,
        blocked = false,
        blockedReason,
        creates = false,
        issues = false,
        reasonPlacement = 'inline',
        disabled,
        children,
        ...props
    },
    ref,
) {
    const { reasonFor } = useGrants();
    const reasonId = useId();

    const gap = reasonFor(ability, ontologyId, { creates, issues });
    // The caller's wording stands in for the permission sentence only; a
    // suspension is the broader fact and keeps its own.
    const why = blocked
        ? (blockedReason || 'Unavailable')
        : gap !== null && reason && gap === gateReason(ability, ontologyId)
            ? reason
            : gap;

    if (!why) {
        return (
            <Button ref={ref} disabled={disabled} {...props}>
                {children}
            </Button>
        );
    }

    const inline = reasonPlacement === 'inline';

    // A disabled button swallows pointer events, so the tooltip listens on the
    // wrapper; the wrapper also carries the reason for anything (a test, a
    // page-level summary) that wants to read it off the DOM.
    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span data-gated-reason={why} className="inline-flex items-center gap-3">
                        <span
                            id={reasonId}
                            className={inline ? 'inline-flex items-center gap-1.5 text-xs text-muted-foreground' : 'sr-only'}
                        >
                            {inline && <LockIcon className="size-3.5 shrink-0" aria-hidden="true" />}
                            {why}
                        </span>
                        <Button ref={ref} {...props} disabled aria-disabled="true" aria-describedby={reasonId}>
                            {children}
                        </Button>
                    </span>
                </TooltipTrigger>
                <TooltipContent>{why}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
});

export default GatedButton;
